import React, { useState, useEffect } from 'react';
import { listPDFTemplates, deletePDFTemplate, downloadPDFPreview, PDFTemplate } from '../api/client';
import { useAuth } from '../context/AuthContext';
import PDFUpload from '../components/PDFUpload';
import './Overview.css';

const FormulareOverview: React.FC = () => {
  const { canEdit, canDelete } = useAuth();
  const [templates, setTemplates] = useState<PDFTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'name' | 'created' | 'fields'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showUpload, setShowUpload] = useState(false);
  const [previewingId, setPreviewingId] = useState<string | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await listPDFTemplates();
      setTemplates(data);
    } catch (err) {
      // Error silently handled
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = (template: PDFTemplate) => {
    setShowUpload(false);
    loadTemplates();
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Möchten Sie das Formular "${name}" wirklich löschen?`)) {
      return;
    }

    try {
      await deletePDFTemplate(id);
      alert('Formular erfolgreich gelöscht');
      loadTemplates(); // Reload list
    } catch (err: any) {
      alert('Fehler beim Löschen: ' + (err.response?.data?.error || err.message));
    }
  };

  const handlePreview = async (template: PDFTemplate) => {
    setPreviewingId(template.id);
    try {
      await downloadPDFPreview(template.id, template.name);
    } catch (err: any) {
      alert('Fehler beim Erstellen der Vorschau: ' + (err.response?.data?.error || err.message));
    } finally {
      setPreviewingId(null);
    }
  };

  const toggleSort = (field: typeof sortBy) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder(field === 'created' ? 'desc' : 'asc');
    }
  };

  const sortedTemplates = [...templates].sort((a, b) => {
    let cmp = 0;
    if (sortBy === 'name') {
      cmp = a.name.localeCompare(b.name, 'de');
    } else if (sortBy === 'fields') {
      cmp = (a.fields?.length || 0) - (b.fields?.length || 0);
    } else {
      cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    }
    return sortOrder === 'asc' ? cmp : -cmp;
  });

  if (loading) {
    return (
      <div className="overview-container">
        <h1>Lade Formulare...</h1>
      </div>
    );
  }

  return (
    <div className="overview-container">
      <div className="overview-header">
        <div>
          <h1>PDF-Formulare</h1>
          <p className="overview-description">
            Alle PDF-Formulare
          </p>
        </div>
        {canEdit && (
          <button className="btn-primary" onClick={() => setShowUpload(!showUpload)}>
            {showUpload ? '✕ Abbrechen' : '+ Neues Formular hochladen'}
          </button>
        )}
      </div>

      {showUpload && (
        <div className="upload-section">
          <PDFUpload onUploadSuccess={handleUploadSuccess} />
        </div>
      )}

      {templates.length > 0 && (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
          <span style={{ fontSize: '0.85rem', color: '#666' }}>Sortieren:</span>
          {([['name', 'Name'], ['created', 'Erstellt'], ['fields', 'Felder']] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => toggleSort(key)}
              className="btn-small"
              style={{
                background: sortBy === key ? '#006355' : '#e0e0e0',
                color: sortBy === key ? 'white' : '#333',
              }}
            >
              {label} {sortBy === key && (sortOrder === 'asc' ? '↑' : '↓')}
            </button>
          ))}
        </div>
      )}

      {templates.length === 0 && !showUpload ? (
        <div className="empty-state">
          <h3>Keine PDF-Formulare vorhanden</h3>
          {canEdit ? (
            <>
              <p>Laden Sie Ihr erstes PDF-Formular hoch, um loszulegen.</p>
              <button className="btn-primary" onClick={() => setShowUpload(true)}>
                Erstes Formular hochladen
              </button>
            </>
          ) : (
            <p>Es wurden noch keine PDF-Formulare hochgeladen.</p>
          )}
        </div>
      ) : templates.length > 0 ? (
        <div className="grid-layout">
          {sortedTemplates.map((template) => (
            <div key={template.id} className="card">
              <div className="card-header">
                <h3>{template.name}</h3>
              </div>
              <div className="card-meta">
                <div>
                  <strong>Felder:</strong> {template.fields?.length || 0}
                </div>
                <div>
                  <strong>Erstellt:</strong>{' '}
                  {new Date(template.created_at).toLocaleDateString('de-DE')}
                </div>
              </div>
              {template.fields && template.fields.length > 0 && (
                <div className="card-details">
                  <details>
                    <summary>Felder anzeigen ({template.fields.length})</summary>
                    <ul className="field-list-compact">
                      {template.fields.map((field) => (
                        <li key={field.name}>
                          <strong>{field.name}</strong>
                          <span className="field-type-small">{field.type}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                </div>
              )}
              <div className="card-actions">
                <button
                  onClick={() => handlePreview(template)}
                  className="btn-small btn-secondary"
                  disabled={previewingId === template.id}
                >
                  {previewingId === template.id ? '⏳ Lädt...' : '👁️ Feldnamen-PDF'}
                </button>
                {canDelete && (
                  <button onClick={() => handleDelete(template.id, template.name)} className="btn-small btn-danger">
                    🗑️ Löschen
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default FormulareOverview;
